import { useState } from "react";
import { CheckIcon } from "@phosphor-icons/react/Check";
import { Button } from "@freightsimple/ui-core/Controls/Button";
import { Image } from "@freightsimple/ui-core/Display/Image";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import type { UploadPhotosPreview } from "../Hooks/useUploadPhotosPreviews";

interface UploadPhotosPhotoViewerProps {
  /** Every photo taken during this visit, oldest first. */
  photos: UploadPhotosPreview[];
  /** How far the photo that's still uploading has got, 0 to 100. */
  uploadPercent: number;
  /** The photo that was attached most recently; it gets the tick. */
  justAttachedUrl: string | undefined;
}

// A ring that fills as the upload goes, drawn over the photo. The photo behind
// is darkened so the white ring reads on any picture, in either theme.
function UploadProgressRing_({
  percent,
  size,
}: {
  percent: number;
  size: "large" | "small";
}) {
  const diameter = size === "large" ? 64 : 28;
  const stroke = size === "large" ? 5 : 3;
  const radius = (diameter - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const filled = (Math.max(0, Math.min(100, percent)) / 100) * circumference;

  return (
    <span
      className="absolute inset-0 flex items-center justify-center bg-black/45"
      data-testid={
        size === "large" ? "upload-photos-upload-progress" : undefined
      }
    >
      <span className="relative flex items-center justify-center">
        <svg
          width={diameter}
          height={diameter}
          aria-hidden
          className="-rotate-90"
        >
          <circle
            cx={diameter / 2}
            cy={diameter / 2}
            r={radius}
            fill="none"
            strokeWidth={stroke}
            className="stroke-white/30"
          />
          <circle
            cx={diameter / 2}
            cy={diameter / 2}
            r={radius}
            fill="none"
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={`${filled} ${circumference}`}
            className="stroke-white transition-[stroke-dasharray] duration-200"
          />
        </svg>
        {size === "large" && (
          <span className="absolute text-sm font-semibold text-white tabular-nums">
            {Math.round(percent)}%
          </span>
        )}
      </span>
    </span>
  );
}

/**
 * The photos taken during this visit: the picked one large, with a row of
 * thumbnails underneath so the shipper can check what they've already taken.
 * A new photo comes into view straight away with its upload progress drawn
 * over it. Once it's attached a tick pops in over it and fades away, once, so
 * paging back to an older photo doesn't replay it.
 */
export function UploadPhotosPhotoViewer(props: UploadPhotosPhotoViewerProps) {
  const total = props.photos.length;
  // Remember which photo was picked and how many there were at the time. Once
  // another photo arrives the pick is stale, so the newest photo shows instead.
  const [picked, setPicked] = useState({ index: 0, total: 0 });
  const index = picked.total === total ? picked.index : total - 1;
  const photo = props.photos[index];
  const [tickPlayedUrl, setTickPlayedUrl] = useState<string | undefined>();

  if (photo === undefined) {
    return undefined;
  }

  return (
    <Stack gap={3} data-testid="upload-photos-photo-viewer">
      <span className="relative overflow-hidden rounded-xl">
        <span
          key={photo.url}
          className="motion-safe:animate-photo-fade-in block"
        >
          <Image
            src={photo.url}
            alt={
              photo.attached
                ? `Attached photo ${index + 1} of ${total}`
                : `Uploading photo ${index + 1} of ${total}`
            }
            maxHeight={280}
            fit="cover"
          />
        </span>
        {!photo.attached && (
          <UploadProgressRing_ percent={props.uploadPercent} size="large" />
        )}
        {photo.attached &&
          photo.url === props.justAttachedUrl &&
          tickPlayedUrl !== photo.url && (
            // Same size and place as the progress ring it replaces. Hidden
            // when motion is reduced: it only exists to animate, and the line
            // above the photo already says it's attached.
            <span
              className="absolute inset-0 flex items-center justify-center motion-reduce:hidden"
              data-testid="upload-photos-attached-tick"
            >
              <span
                className="motion-safe:animate-check-pop flex size-16 items-center justify-center rounded-full bg-dark-green text-white shadow-lg"
                onAnimationEnd={() => setTickPlayedUrl(photo.url)}
              >
                <CheckIcon weight="bold" size={32} aria-hidden />
              </span>
            </span>
          )}
      </span>
      {total > 1 && (
        // The row scrolls sideways, which clips anything past its edges, and
        // the picked thumbnail's outline is drawn just outside it. The padding
        // makes room for the outline; the negative margin keeps the thumbnails
        // lined up with the photo above.
        <div className="-mx-1 flex gap-2 overflow-x-auto p-1">
          {props.photos.map((thumbnail, thumbnailIndex) => (
            <Button
              key={thumbnail.url}
              size="link"
              onClick={() => setPicked({ index: thumbnailIndex, total })}
              data-testid="upload-photos-thumbnail"
            >
              <span
                className={`motion-safe:animate-photo-fade-in relative block size-16 shrink-0 overflow-hidden rounded-lg ring-2 transition ${
                  thumbnailIndex === index
                    ? "ring-brand"
                    : "opacity-60 ring-transparent hover:opacity-100"
                }`}
              >
                <Image
                  src={thumbnail.url}
                  alt={`Show photo ${thumbnailIndex + 1}`}
                  width={64}
                  height={64}
                  fit="cover"
                />
                {!thumbnail.attached && (
                  <UploadProgressRing_
                    percent={props.uploadPercent}
                    size="small"
                  />
                )}
              </span>
            </Button>
          ))}
        </div>
      )}
    </Stack>
  );
}
